import React, { useEffect, useReducer, useCallback } from 'react';
import Swal from 'sweetalert2';
import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import LoadingDialog from '../components/common/LoadingDialog';
import consts from '../consts';
import strings from "../Globalization/index";
import connectionService from "../services/Connection";
import util from '../util/index';
import PrintConfigForm from '../AppGrid/PrintConfigForm';
import { useBreadcrumbs } from '../contexts/Breadcrumbs';
import { usePrerequisites } from '../contexts/Prerequisites';


const PrintConfig = ({ 
	url = connectionService.PrintConfig,
	comboTypes = ['CycleGraphSize', 'ContentDisplayType', 'BarcodeConfig']
}) => {
	const location = useLocation();
	const theme = useTheme();
	const [breadcrumbs, setBreadcrumbs] = useBreadcrumbs();
	const [state, setState] = useReducer(
		(prev, next) => (typeof next === 'function' ? { ...prev, ...next(prev) } : { ...prev, ...next }),
		{
			loading: false,
			loadingMessage: '',
			activeRecord: { record: {}, combos: {} }
		}
	);
	const [{ strings }, { getLicenseType }] = usePrerequisites();
	const handleOnSubmit = useCallback((values, { resetForm }) => {
		setState({ loading: true });
		const formData = new FormData();
		const comboTypesArr = Array.isArray(comboTypes)
			? comboTypes.map((comboType) => ({ type: comboType, loaded: true }))
			: [];

		for (const [key, value] of Object.entries(values)) {
			formData.append(key, value);
		}
		formData.append('id', values._id);
		formData.append('action', 'save');
		formData.append('comboTypes', JSON.stringify(comboTypesArr));
		fetch(url, {
			method: 'POST',
			body: formData
		})
			.then(() => {
				setState({ loading: false });
				Swal.fire({ titleText: strings.Success, text: strings.ConfigurationSaveSuccessText, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
			})
			.finally(() => setState({ loading: false }));
	}, [state.activeRecord.record]);

	const getFormGroupsVisibility = useCallback(
		(json = {}) => {
			const licenseType = getLicenseType();
			const hasCATExport = licenseType?.products && licenseType?.products.includes('catexport');
			const hasCATViewer = licenseType?.products && licenseType?.products.includes('catviewer');
			const isCatEndo = licenseType?.products?.includes('catendo');
			const formGroupsVisibility = new Map([
				['HeaderFooter', hasCATExport || hasCATViewer],
				['PrintConfig', hasCATExport || hasCATViewer ],
				['PrintBarcodeConfiguration', isCatEndo]]);
			return formGroupsVisibility;
		},
		[location.pathname, getLicenseType]
	);

	const fetchData = () => {
		setState({ loading: true });
		const printConfigId = util.localData?.loginData?.tags?.PrintConfigId || null;
		const comboTypesArr = Array.isArray(comboTypes)
			? comboTypes.map((comboType) => ({ type: comboType, loaded: true }))
			: [];
		const formData = new FormData();
		formData.append('action', 'load');
		formData.append('comboTypes', JSON.stringify(comboTypesArr));
		formData.append('id', !consts.isNodeServer ? 1 : printConfigId);

		fetch(url, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.json())
			.then((json) => {
				const { success, data, combos } = json;
				const formGroupsVisibility = getFormGroupsVisibility(json);
				if (success) {
					setState({
						activeRecord: { combos, record: data, formGroupsVisibility, },
						loading: false
					});
				}
			})
			.finally(() => setState({ loading: false }));
	};

	useEffect(() => {
		setBreadcrumbs({ docTitle: strings.PrintConfig, breadcrumbs: [{ text: strings.PrintConfig }] });
		fetchData()
	}, []);

	return (
		<Grid container>
			<Grid size={12}>
				<PrintConfigForm
					onSubmit={handleOnSubmit}
					activeRecord={state.activeRecord}
					initialValues={state.activeRecord.record}
					hideCancel
				/>

				{state.loading && <LoadingDialog isOpen message={state.loadingMessage} />}
			</Grid>
		</Grid>
	);
};


export default PrintConfig;